import { mergeAttributes, Node } from "@tiptap/react"
import { ReactNodeViewRenderer } from "@tiptap/react"
import { Fragment, Slice, type Node as ProseMirrorNode } from '@tiptap/pm/model'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { toast } from 'sonner'
import { ImageUploadNode as ImageUploadNodeComponent } from "@/tiptap/tiptap-node/image-upload-node/image-upload-node"
import { handleImageUpload } from '@/tiptap/lib/tiptap-utils'

export const CLIPBOARD_IMAGE_ERROR =
  'This page did not provide image data. Copy the image directly or choose a file.'

const PASTED_DATA_IMAGE_MARKER = 'glyde-pasted-data-image:'

export interface ImageUploadNodeStorage {
  pendingFiles: Map<string, File>
  startedUploads: Set<string>
  abortControllers: Map<string, AbortController>
  completedUploads: Map<string, { url: string; filename: string }>
}

function isClipboardImage(file: File): boolean {
  return (
    file.type.startsWith('image/') ||
    /\.(?:jpe?g|png|gif|webp|svg|bmp|tiff?)$/i.test(file.name)
  )
}

function getExtensionForMimeType(mimeType: string): string | undefined {
  return {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/svg+xml': 'svg',
    'image/bmp': 'bmp',
    'image/tiff': 'tiff',
  }[mimeType]
}

function normalizeClipboardImageFile(file: File, index: number): File {
  if (/\.(?:jpe?g|png|gif|webp|svg|bmp|tiff?)$/i.test(file.name)) {
    return file
  }

  const extension = getExtensionForMimeType(file.type)

  if (!extension) return file

  return new File([file], `pasted-image-${index + 1}.${extension}`, {
    type: file.type,
    lastModified: file.lastModified,
  })
}

export function dataImageUrlToFile(
  source: string,
  index: number,
  maxSize = 0
): File | null {
  const match = source.match(
    /^data:(image\/[^;,]+)(?:;charset=[^;,]+)?(;base64)?,([\s\S]*)$/i
  )
  if (!match) return null

  const [, mimeType, base64Marker, encodedData] = match
  const extension = getExtensionForMimeType(mimeType.toLowerCase())
  if (!extension) return null

  const estimatedSize = base64Marker
    ? Math.ceil(encodedData.length * 0.75)
    : encodedData.length * 4
  if (maxSize > 0 && estimatedSize > maxSize) return null

  try {
    const blobPart = base64Marker
      ? Uint8Array.from(atob(encodedData), (character) =>
          character.charCodeAt(0)
        )
      : new TextEncoder().encode(decodeURIComponent(encodedData))

    if (maxSize > 0 && blobPart.byteLength > maxSize) return null

    return new File([blobPart], `pasted-image-${index + 1}.${extension}`, {
      type: mimeType.toLowerCase(),
    })
  } catch {
    return null
  }
}

export function getClipboardImageFiles(
  clipboardData: DataTransfer | null | undefined
): File[] {
  if (!clipboardData) return []

  const files = Array.from(clipboardData.files || []).filter(isClipboardImage)
  if (files.length > 0) return files

  return Array.from(clipboardData.items || []).flatMap((item) => {
    if (item.kind !== 'file') return []
    const file = item.getAsFile()
    return file && isClipboardImage(file) ? [file] : []
  })
}

function countImageNodes(fragment: Fragment): number {
  let count = 0

  fragment.forEach((node) => {
    if (node.type.name === 'image') {
      count += 1
      return
    }

    if (node.content.size > 0) {
      count += countImageNodes(node.content)
    }
  })

  return count
}

function findUploadNode(
  documentNode: ProseMirrorNode,
  uploadId: string
): { node: ProseMirrorNode; pos: number } | null {
  let result: { node: ProseMirrorNode; pos: number } | null = null

  documentNode.descendants((node, pos) => {
    if (
      result === null &&
      node.type.name === 'imageUpload' &&
      node.attrs.uploadId === uploadId
    ) {
      result = { node, pos }
      return false
    }
  })

  return result
}

export function findUnuploadedImagePos(
  documentNode: ProseMirrorNode
): number | null {
  let result: number | null = null

  documentNode.descendants((node, pos) => {
    if (result !== null || node.type.name !== 'image') return

    const source = String(node.attrs.src || '').trimStart()
    if (/^(?:data|blob):/i.test(source)) {
      result = pos
    }
  })

  return result
}

export type UploadFunction = (
  file: File,
  onProgress?: (event: { progress: number }) => void,
  abortSignal?: AbortSignal
) => Promise<string>

export interface ImageUploadNodeOptions {
  /**
   * Acceptable file types for upload.
   * @default 'image/*'
   */
  accept?: string
  /**
   * Maximum number of files that can be uploaded.
   * @default 1
   */
  limit?: number
  /**
   * Maximum file size in bytes (0 for unlimited).
   * @default 0
   */
  maxSize?: number
  /**
   * Function to handle the upload process.
   */
  upload?: UploadFunction
  /**
   * Callback for upload errors.
   */
  onError?: (error: Error) => void
  /**
   * Callback for successful uploads.
   */
  onSuccess?: (url: string) => void
}

declare module "@tiptap/react" {
  interface Commands<ReturnType> {
    imageUpload: {
      setImageUploadNode: (options?: ImageUploadNodeOptions) => ReturnType
    }
  }
}

/**
 * A TipTap node extension that creates an image upload component.
 * @see registry/tiptap-node/image-upload-node/image-upload-node
 */
export const ImageUploadNode = Node.create<
  ImageUploadNodeOptions,
  ImageUploadNodeStorage
>({
  name: "imageUpload",

  group: "block",

  draggable: true,

  selectable: true,

  atom: true,

  addOptions() {
    return {
      accept: "image/*",
      limit: 1,
      maxSize: 0,
      // 默认上传实现：将文件 POST 到 /trpc/file.upload，返回 JSON { url }
      // 若调用方在 configure() 中传入 upload，则会覆盖此实现。
      upload: async (
        file: File,
        _onProgress?: (e: { progress: number }) => void,
        abortSignal?: AbortSignal
      ): Promise<string> => {
        // Delegate to shared uploader (handles auth headers, XHR, tRPC shape)
        return handleImageUpload(file, _onProgress, abortSignal)
      },
      onError: undefined,
      onSuccess: undefined,
    }
  },

  addStorage() {
    return {
      pendingFiles: new Map<string, File>(),
      startedUploads: new Set<string>(),
      abortControllers: new Map<string, AbortController>(),
      completedUploads: new Map<string, { url: string; filename: string }>(),
    }
  },

  addAttributes() {
    return {
      accept: {
        default: this.options.accept,
      },
      limit: {
        default: this.options.limit,
      },
      maxSize: {
        default: this.options.maxSize,
      },
      uploadId: {
        default: null,
        renderHTML: () => ({}),
      },
      clipboardError: {
        default: null,
        renderHTML: () => ({}),
      },
    }
  },

  parseHTML() {
    return [{ tag: 'div[data-type="image-upload"]' }]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes({ "data-type": "image-upload" }, HTMLAttributes),
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(ImageUploadNodeComponent)
  },

  addProseMirrorPlugins() {
    const editor = this.editor
    const uploadNodeType = this.type
    const storage = this.storage
    const limit = this.options.limit
    const maxSize = this.options.maxSize || 0
    const onError = this.options.onError
    const pastedDataImages = new Map<string, string>()

    return [
      new Plugin({
        key: new PluginKey('imageUploadPaste'),
        props: {
          transformPastedHTML(html) {
            pastedDataImages.clear()

            if (!/data:image\//i.test(html)) return html

            const documentNode = new DOMParser().parseFromString(
              html,
              'text/html'
            )

            documentNode.querySelectorAll('img[src]').forEach((image) => {
              const source = image.getAttribute('src')?.trimStart()
              if (!source || !/^data:image\//i.test(source)) return

              const marker = `${PASTED_DATA_IMAGE_MARKER}${crypto.randomUUID()}`
              pastedDataImages.set(marker, source)
              image.setAttribute('src', marker)
            })

            return documentNode.body.innerHTML
          },
          handlePaste(view, event, slice) {
            const dataImageSources = new Map(pastedDataImages)
            pastedDataImages.clear()

            if (!editor.isEditable) return false

            const files = getClipboardImageFiles(event.clipboardData).map(
              normalizeClipboardImageFile
            )
            const pastedImageCount = countImageNodes(slice.content)

            if (files.length === 0 && pastedImageCount === 0) return false

            const limitError =
              limit && Math.max(files.length, pastedImageCount) > limit
                ? `Maximum ${limit} images can be pasted at once.`
                : null
            const uploadFiles = limitError ? [] : files

            if (limitError) {
              onError?.(new Error(limitError))
            }

            let fileIndex = 0
            let hasOversizedFile = false
            const pendingBlobSources: Array<{
              uploadId: string
              source: string
              index: number
            }> = []
            const createUploadNode = (
              file?: File,
              error = limitError,
              uploadId = crypto.randomUUID()
            ) => {

              if (file) storage.pendingFiles.set(uploadId, file)

              return uploadNodeType.create({
                uploadId,
                clipboardError: error || (file ? null : CLIPBOARD_IMAGE_ERROR),
              })
            }

            const replaceImages = (fragment: Fragment): Fragment => {
              const nodes: ProseMirrorNode[] = []

              fragment.forEach((node) => {
                if (node.type.name === 'image') {
                  let file = uploadFiles[fileIndex]
                  const parsedSource = String(node.attrs.src || '')
                  const source =
                    dataImageSources.get(parsedSource) || parsedSource

                  if (file && maxSize > 0 && file.size > maxSize) {
                    hasOversizedFile = true
                    fileIndex += 1
                    return
                  }

                  if (!file && !limitError) {
                    file =
                      dataImageUrlToFile(source, fileIndex, maxSize) ||
                      undefined
                  }

                  if (!file && !limitError && source.startsWith('blob:')) {
                    const uploadId = crypto.randomUUID()
                    pendingBlobSources.push({
                      uploadId,
                      source,
                      index: fileIndex,
                    })
                    nodes.push(
                      createUploadNode(undefined, CLIPBOARD_IMAGE_ERROR, uploadId)
                    )
                    fileIndex += 1
                    return
                  }

                  nodes.push(createUploadNode(file))
                  fileIndex += 1
                  return
                }

                nodes.push(
                  node.content.size > 0
                    ? node.copy(replaceImages(node.content))
                    : node
                )
              })

              return Fragment.fromArray(nodes)
            }

            const transformedContent = replaceImages(slice.content)
            const remainingUploadNodes = uploadFiles
              .slice(fileIndex)
              .flatMap((file) => {
                if (maxSize > 0 && file.size > maxSize) {
                  hasOversizedFile = true
                  return []
                }
                return [createUploadNode(file)]
              })

            if (
              limitError &&
              transformedContent.size === 0 &&
              remainingUploadNodes.length === 0
            ) {
              remainingUploadNodes.push(createUploadNode())
            }

            let transaction = view.state.tr

            if (transformedContent.size > 0) {
              transaction = transaction.replaceSelection(
                new Slice(transformedContent, slice.openStart, slice.openEnd)
              )
            }

            if (remainingUploadNodes.length > 0) {
              transaction = transaction.replaceSelection(
                new Slice(Fragment.fromArray(remainingUploadNodes), 0, 0)
              )
            }

            event.preventDefault()
            view.dispatch(transaction.scrollIntoView())

            if (hasOversizedFile) {
              toast.error(
                `File size exceeds maximum allowed (${maxSize / 1024 / 1024}MB)`
              )
            }

            pendingBlobSources.forEach(({ uploadId, source, index }) => {
              void fetch(source)
                .then((response) => {
                  if (!response.ok) throw new Error('Blob image is unavailable')
                  return response.blob()
                })
                .then((blob) => {
                  if (!blob.type.startsWith('image/')) {
                    throw new Error('Blob URL did not contain an image')
                  }
                  if (maxSize > 0 && blob.size > maxSize) {
                    throw new Error('Blob image exceeds the upload size limit')
                  }

                  const file = normalizeClipboardImageFile(
                    new File([blob], '', { type: blob.type }),
                    index
                  )
                  if (editor.isDestroyed) return
                  storage.pendingFiles.set(uploadId, file)
                  const result = findUploadNode(editor.state.doc, uploadId)
                  if (!result) return

                  editor.view.dispatch(
                    editor.state.tr
                      .setNodeMarkup(result.pos, undefined, {
                        ...result.node.attrs,
                        clipboardError: null,
                      })
                      .setMeta('addToHistory', false)
                  )
                })
                .catch(() => {
                  // Keep the existing inline fallback when a blob URL has expired
                  // or belongs to another browser context.
                })
            })

            return true
          },
        },
      }),
    ]
  },

  addCommands() {
    return {
      setImageUploadNode:
        (options = {}) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: options,
          })
        },
    }
  },

  /**
   * Adds Enter key handler to trigger the upload component when it's selected.
   */
  addKeyboardShortcuts() {
    return {
      Enter: ({ editor }) => {
        const { selection } = editor.state
        const { nodeAfter } = selection.$from

        if (
          nodeAfter &&
          nodeAfter.type.name === "imageUpload" &&
          editor.isActive("imageUpload")
        ) {
          const nodeEl = editor.view.nodeDOM(selection.$from.pos)
          if (nodeEl && nodeEl instanceof HTMLElement) {
            // Since NodeViewWrapper is wrapped with a div, we need to click the first child
            const firstChild = nodeEl.firstChild
            if (firstChild && firstChild instanceof HTMLElement) {
              firstChild.click()
              return true
            }
          }
        }
        return false
      },
    }
  },
})

export default ImageUploadNode
